// Group Chat Component
// Real-time chat messaging within groups

import { useState, useRef, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { MessagesSquare, Loader2, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { 
  getGroupChat, sendGroupChatMessage, deleteGroupChatMessage, 
  getNewGroupChatMessages, resolveImageUrl 
} from "@/lib/api";

export default function GroupChat({ groupId, currentUser }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const lastMessageTime = useRef(null);

  useEffect(() => {
    loadMessages();
  }, [groupId]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (lastMessageTime.current) pollNewMessages();
    }, 3000);
    return () => clearInterval(interval);
  }, [groupId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const loadMessages = async () => {
    setLoading(true);
    try {
      const res = await getGroupChat(groupId);
      setMessages(res.data.messages || []);
      if (res.data.messages?.length > 0) {
        lastMessageTime.current = res.data.messages[res.data.messages.length - 1].created_at;
      }
    } catch (err) {
      console.error("Failed to load chat:", err);
    }
    setLoading(false);
  };

  const pollNewMessages = async () => {
    try {
      const res = await getNewGroupChatMessages(groupId, lastMessageTime.current);
      if (res.data.messages?.length > 0) {
        setMessages(prev => [...prev, ...res.data.messages]);
        lastMessageTime.current = res.data.messages[res.data.messages.length - 1].created_at;
      }
    } catch {}
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const res = await sendGroupChatMessage(groupId, newMessage);
      setMessages(prev => [...prev, res.data]);
      lastMessageTime.current = res.data.created_at;
      setNewMessage("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send message");
    }
    setSending(false);
  };

  const handleDelete = async (messageId) => {
    try {
      await deleteGroupChatMessage(groupId, messageId);
      setMessages(prev => prev.filter(m => m.message_id !== messageId));
      toast.success("Message deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete message");
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + 
           date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const groupMessagesByDate = (messages) => {
    const groups = {};
    messages.forEach(msg => {
      const date = new Date(msg.created_at).toLocaleDateString();
      if (!groups[date]) groups[date] = [];
      groups[date].push(msg);
    });
    return groups;
  };

  const messageGroups = groupMessagesByDate(messages);

  if (loading) {
    return (
      <Card className="bg-white border-[#E5E0D5]">
        <CardContent className="py-12 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-[var(--liturgical-primary)]" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border-[#E5E0D5] flex flex-col" style={{ height: "500px" }}>
      <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chat-messages-container">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <MessagesSquare className="w-12 h-12 text-[#E5E0D5] mb-4" />
            <h3 className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26] mb-2">
              Start the conversation
            </h3>
            <p className="text-sm text-[#6B665E] font-['Nunito']">
              Be the first to send a message in this group chat!
            </p>
          </div>
        ) : (
          Object.entries(messageGroups).map(([date, msgs]) => (
            <div key={date}>
              <div className="flex items-center gap-2 my-4">
                <div className="flex-1 h-px bg-[#E5E0D5]"></div>
                <span className="text-xs text-[#6B665E] font-['Nunito'] px-2">{date}</span>
                <div className="flex-1 h-px bg-[#E5E0D5]"></div>
              </div>
              
              {msgs.map((msg, idx) => {
                const isOwn = msg.sender_id === currentUser?.user_id;
                const showAvatar = idx === 0 || msgs[idx - 1].sender_id !== msg.sender_id;
                
                return (
                  <div 
                    key={msg.message_id} 
                    className={`flex gap-2 mb-2 ${isOwn ? 'flex-row-reverse' : ''}`}
                    data-testid={`chat-message-${msg.message_id}`}
                  >
                    {showAvatar ? (
                      <Avatar className="h-8 w-8 flex-shrink-0">
                        <AvatarImage src={resolveImageUrl(msg.sender_picture)} />
                        <AvatarFallback className={`text-xs text-white ${isOwn ? 'bg-[#556B2F]' : 'bg-[var(--liturgical-primary)]'}`}>
                          {msg.sender_name?.[0]?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    ) : (
                      <div className="w-8 flex-shrink-0"></div>
                    )}
                    
                    <div className={`group max-w-[70%] ${isOwn ? 'items-end' : 'items-start'}`}>
                      {showAvatar && !isOwn && (
                        <span className="text-xs text-[#6B665E] font-['Nunito'] ml-1 mb-1 block">
                          {msg.sender_name}
                        </span>
                      )}
                      <div className="flex items-end gap-1">
                        <div 
                          className={`rounded-2xl px-3 py-2 ${
                            isOwn 
                              ? 'bg-[#556B2F] text-white rounded-br-sm' 
                              : 'bg-[#F9F7F2] text-[#2D2A26] rounded-bl-sm'
                          }`}
                        >
                          <p className="text-sm font-['Nunito'] whitespace-pre-wrap break-words">
                            {msg.content}
                          </p>
                        </div>
                        
                        {isOwn && (
                          <button
                            onClick={() => handleDelete(msg.message_id)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-50 rounded"
                            title="Delete message"
                          >
                            <Trash2 className="w-3 h-3 text-red-400" />
                          </button>
                        )}
                      </div>
                      <span className={`text-[10px] text-[#6B665E] font-['Nunito'] mt-1 block ${isOwn ? 'text-right mr-1' : 'ml-1'}`}>
                        {formatTime(msg.created_at)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>
      
      <div className="border-t border-[#E5E0D5] p-3">
        <form onSubmit={handleSend} className="flex gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 border-[#E5E0D5]"
            data-testid="chat-input"
          />
          <Button 
            type="submit" 
            disabled={sending || !newMessage.trim()}
            className="bg-[var(--liturgical-primary)] hover:brightness-90"
            data-testid="chat-send-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </form>
      </div>
    </Card>
  );
}
